import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useEvent } from '../contexts/EventContext';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, AlertCircle, CheckCircle, ChevronDown, User } from 'lucide-react';

const API_BASE_URL = '/api';

const SUPPORTED_LANGS = ['fr', 'de', 'en'];
const LANG_LABELS = { fr: 'Français', de: 'Deutsch', en: 'English' };

const EUROPEAN_CODES = new Set([
  'AL','AD','AT','BY','BE','BA','BG','HR','CY','CZ','DK','EE','FI','FR',
  'DE','GR','HU','IS','IE','IT','XK','LV','LI','LT','LU','MT','MD','MC',
  'ME','NL','MK','NO','PL','PT','RO','RU','SM','RS','SK','SI','ES','SE',
  'CH','TR','UA','GB','VA'
]);

const I18N = {
  fr: {
    // Code événement
    event_code_title: 'Code de participation',
    event_code_welcome: 'Inscrivez-vous',
    event_code_registered: 'Accédez à l\'événement avec votre code d\'inscription ou en payant votre participation.',
    event_code_placeholder: 'Ex : LFTD-X7K2',
    event_code_btn: 'Valider le code',
    event_code_validating: 'Validation...',
    event_code_no_code: 'Pas de code ?',
    event_code_contact: 'Contactez-nous',
    event_code_invalid: 'Code invalide ou inactif. Vérifiez et réessayez.',
    event_code_wrong_event: "Ce code n'est pas valide pour cet événement.",
    event_code_full: 'Ce code a atteint le nombre maximum de participants.',
    event_code_error: 'Erreur lors de la validation. Réessayez.',
    event_code_have_code: 'J\'ai un code d\'accès',
    event_code_or: 'ou',
    event_code_pay: 'Payer ma participation',
    event_code_pay_sub: 'Paiement sécurisé en ligne',
    event_code_pay_soon: 'Paiement en ligne bientôt disponible. Contactez-nous pour vous inscrire.',
    // Vérification
    verify_title: 'Vérification',
    verify_subtitle: 'Entrez le code à 6 chiffres envoyé à',
    verify_btn: 'Vérifier le code',
    verifying: 'Vérification...',
    no_code: 'Vous n\'avez pas reçu le code ?',
    resend: 'Renvoyer le code',
    resending: 'Envoi...',
    resend_success: 'Un nouveau code a été envoyé à votre email',
    spam_tip: 'Astuce : Vérifiez votre dossier spam si vous ne voyez pas l\'email',
    back: 'Retour',
    // Profil
    profile_title: 'Complétez votre profil',
    profile_subtitle: 'Ces informations seront visibles par les autres participants',
    firstname: 'Prénom *',
    lastname: 'Nom *',
    fonction: 'Fonction / Poste *',
    description: 'Description',
    description_placeholder: 'Décrivez votre activité et ce que vous recherchez...',
    country: 'Pays',
    country_placeholder: '-- Sélectionner un pays --',
    country_europe: '— Europe —',
    country_world: '— Monde —',
    linkedin: 'Profil LinkedIn',
    phone: 'Téléphone mobile',
    complete_btn: 'Accéder à l\'événement',
    completing: 'Enregistrement...',
    required_error: 'Veuillez remplir les champs obligatoires (Prénom, Nom, Fonction)',
    save_error: 'Erreur lors de l\'enregistrement du profil',
    lang_label: 'Langue',
    terms_agree: 'J\'accepte les',
    terms_link: 'conditions générales d\'utilisation',
    terms_required: 'Veuillez accepter les conditions générales pour continuer',
    // Pré-inscription
    pre_register_btn: 'Demander à rejoindre',
    pre_register_sub: 'Pré-inscription à l\'événement',
    pre_register_title: 'Demande d\'inscription',
    pre_register_subtitle: 'Remplissez ce formulaire. Votre demande sera examinée et si elle est validée, vous recevrez votre code d\'accès par email.',
    pre_register_company: 'Société / Entreprise *',
    pre_register_submit: 'Envoyer ma demande',
    pre_register_submitting: 'Envoi en cours...',
    pre_register_success_title: 'Demande bien reçue !',
    pre_register_success_msg: 'Votre pré-inscription a été enregistrée. Elle sera analysée et si elle est validée, vous recevrez votre code d\'accès par email.',
    pre_register_required: 'Veuillez remplir les champs obligatoires (Prénom, Nom, Société, Fonction)',
    pre_register_error: 'Erreur lors de l\'envoi. Réessayez.',
    // Inscriptions fermées
    registration_closed_title: 'Inscriptions fermées',
    registration_closed_msg: 'Les inscriptions à cet événement sont actuellement fermées. Contactez l\'organisateur pour plus d\'informations.',
  },
  de: {
    // Code événement
    event_code_title: 'Teilnahmecode',
    event_code_welcome: 'Registrieren Sie sich',
    event_code_registered: 'Greifen Sie auf die Veranstaltung mit Ihrem Zugangscode zu oder bezahlen Sie Ihre Teilnahme.',
    event_code_placeholder: 'Bsp: LFTD-X7K2',
    event_code_btn: 'Code bestätigen',
    event_code_validating: 'Wird geprüft...',
    event_code_no_code: 'Kein Code?',
    event_code_contact: 'Kontaktieren Sie uns',
    event_code_invalid: 'Ungültiger oder inaktiver Code. Bitte prüfen und erneut versuchen.',
    event_code_wrong_event: 'Dieser Code ist für diese Veranstaltung nicht gültig.',
    event_code_full: 'Dieser Code hat die maximale Teilnehmerzahl erreicht.',
    event_code_error: 'Fehler bei der Validierung. Bitte erneut versuchen.',
    event_code_have_code: 'Ich habe einen Zugangscode',
    event_code_or: 'oder',
    event_code_pay: 'Teilnahme bezahlen',
    event_code_pay_sub: 'Sicherer Online-Zahlung',
    event_code_pay_soon: 'Online-Zahlung bald verfügbar. Kontaktieren Sie uns zur Anmeldung.',
    // Vérification
    verify_title: 'Verifizierung',
    verify_subtitle: 'Geben Sie den 6-stelligen Code ein, der an gesendet wurde',
    verify_btn: 'Code bestätigen',
    verifying: 'Wird verifiziert...',
    no_code: 'Sie haben keinen Code erhalten?',
    resend: 'Code erneut senden',
    resending: 'Senden...',
    resend_success: 'Ein neuer Code wurde an Ihre E-Mail gesendet',
    spam_tip: 'Tipp: Überprüfen Sie Ihren Spam-Ordner, falls Sie die E-Mail nicht sehen',
    back: 'Zurück',
    profile_title: 'Vervollständigen Sie Ihr Profil',
    profile_subtitle: 'Diese Informationen werden für andere Teilnehmer sichtbar sein',
    firstname: 'Vorname *',
    lastname: 'Nachname *',
    fonction: 'Funktion / Position *',
    description: 'Beschreibung',
    description_placeholder: 'Beschreiben Sie Ihre Tätigkeit und was Sie suchen...',
    country: 'Land',
    country_placeholder: '-- Land auswählen --',
    country_europe: '— Europa —',
    country_world: '— Welt —',
    linkedin: 'LinkedIn-Profil',
    phone: 'Handynummer',
    complete_btn: 'Zum Event',
    completing: 'Wird gespeichert...',
    required_error: 'Bitte füllen Sie die Pflichtfelder aus (Vorname, Nachname, Funktion)',
    save_error: 'Fehler beim Speichern des Profils',
    lang_label: 'Sprache',
    terms_agree: 'Ich akzeptiere die',
    terms_link: 'Allgemeinen Nutzungsbedingungen',
    terms_required: 'Bitte akzeptieren Sie die AGB, um fortzufahren',
    // Pré-inscription
    pre_register_btn: 'Beitritt beantragen',
    pre_register_sub: 'Voranmeldung zur Veranstaltung',
    pre_register_title: 'Anmeldungsantrag',
    pre_register_subtitle: 'Füllen Sie dieses Formular aus. Ihr Antrag wird geprüft und bei Genehmigung erhalten Sie Ihren Zugangscode per E-Mail.',
    pre_register_company: 'Unternehmen *',
    pre_register_submit: 'Anfrage absenden',
    pre_register_submitting: 'Wird gesendet...',
    pre_register_success_title: 'Anfrage eingegangen!',
    pre_register_success_msg: 'Ihre Voranmeldung wurde registriert. Sie wird geprüft und bei Genehmigung erhalten Sie Ihren Zugangscode per E-Mail.',
    pre_register_required: 'Bitte füllen Sie die Pflichtfelder aus (Vorname, Nachname, Unternehmen, Funktion)',
    pre_register_error: 'Fehler beim Senden. Bitte erneut versuchen.',
    // Inscriptions fermées
    registration_closed_title: 'Anmeldung geschlossen',
    registration_closed_msg: 'Die Anmeldungen für diese Veranstaltung sind derzeit geschlossen. Kontaktieren Sie den Veranstalter für weitere Informationen.',
  },
  en: {
    // Event code
    event_code_title: 'Participation code',
    event_code_welcome: 'Welcome!',
    event_code_registered: 'Already registered for this event? Please enter the code provided at registration.',
    event_code_placeholder: 'E.g.: LFTD-X7K2',
    event_code_btn: 'Validate code',
    event_code_validating: 'Validating...',
    event_code_no_code: 'No code?',
    event_code_contact: 'Contact us',
    event_code_invalid: 'Invalid or inactive code. Please check and try again.',
    event_code_wrong_event: 'This code is not valid for this event.',
    event_code_full: 'This code has reached the maximum number of participants.',
    event_code_error: 'Validation error. Please try again.',
    event_code_have_code: 'I have an access code',
    event_code_or: 'or',
    event_code_pay: 'Pay to access',
    event_code_pay_sub: 'Secure online payment',
    event_code_pay_soon: 'Online payment coming soon. Contact us to register.',
    // Verification
    verify_title: 'Verification',
    verify_subtitle: 'Enter the 6-digit code sent to',
    verify_btn: 'Verify code',
    verifying: 'Verifying...',
    no_code: "Didn't receive the code?",
    resend: 'Resend code',
    resending: 'Sending...',
    resend_success: 'A new code has been sent to your email',
    spam_tip: 'Tip: Check your spam folder if you don\'t see the email',
    back: 'Back',
    profile_title: 'Complete your profile',
    profile_subtitle: 'This information will be visible to other participants',
    firstname: 'First name *',
    lastname: 'Last name *',
    fonction: 'Function / Position *',
    description: 'Description',
    description_placeholder: 'Describe your activity and what you are looking for...',
    country: 'Country',
    country_placeholder: '-- Select a country --',
    country_europe: '— Europe —',
    country_world: '— World —',
    linkedin: 'LinkedIn profile',
    phone: 'Mobile phone',
    complete_btn: 'Access the event',
    completing: 'Saving...',
    required_error: 'Please fill in required fields (First name, Last name, Function)',
    save_error: 'Error saving profile',
    lang_label: 'Language',
    terms_agree: 'I agree with the',
    terms_link: 'terms and conditions',
    terms_required: 'Please accept the terms and conditions to continue',
    // Pre-registration
    pre_register_btn: 'Request to join',
    pre_register_sub: 'Pre-register for the event',
    pre_register_title: 'Registration request',
    pre_register_subtitle: 'Fill in this form. Your request will be reviewed and if approved, you will receive your access code by email.',
    pre_register_company: 'Company *',
    pre_register_submit: 'Send my request',
    pre_register_submitting: 'Sending...',
    pre_register_success_title: 'Request received!',
    pre_register_success_msg: 'Your pre-registration has been recorded. It will be reviewed and if approved, you will receive your access code by email.',
    pre_register_required: 'Please fill in required fields (First name, Last name, Company, Function)',
    pre_register_error: 'Error sending request. Please try again.',
    // Registration closed
    registration_closed_title: 'Registration closed',
    registration_closed_msg: 'Registrations for this event are currently closed. Please contact the organizer for more information.',
  },
};

// Bloc "Payer" — affiche un message "bientôt disponible" + lien contact
const PayBlock = ({ i }) => {
  const [showSoon, setShowSoon] = useState(false);
  return (
    <div className="border border-gray-200 rounded-xl p-5">
      <p className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-purple-100 text-purple-600 text-xs font-bold">2</span>
        {i.event_code_pay}
      </p>
      {showSoon ? (
        <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
          <p className="text-sm text-purple-700">{i.event_code_pay_soon}</p>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setShowSoon(true)}
          className="w-full border-2 border-purple-500 text-purple-600 py-3 rounded-lg font-semibold hover:bg-purple-50 transition flex items-center justify-center gap-2"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
            <line x1="1" y1="10" x2="23" y2="10"/>
          </svg>
          <span>{i.event_code_pay}</span>
          <span className="text-xs text-purple-400 ml-1">— {i.event_code_pay_sub}</span>
        </button>
      )}
    </div>
  );
};

const getInitialLang = (fromState) => {
  if (SUPPORTED_LANGS.includes(fromState)) return fromState;
  const saved = localStorage.getItem('language');
  if (SUPPORTED_LANGS.includes(saved)) return saved;
  const browser = (navigator.language || 'fr').slice(0, 2).toLowerCase();
  return SUPPORTED_LANGS.includes(browser) ? browser : 'fr';
};

const VerifyCodePage = () => {
  const { event } = useEvent();
  const { verifyCode, sendCode } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { slug } = useParams();
  const eventSlug = slug || event?.slug || '';

  // État de l'étape : 'verify' | 'event_code' | 'profile'
  const [step, setStep] = useState('verify');

  // Code
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  const inputRefs = [
    useRef(null), useRef(null), useRef(null),
    useRef(null), useRef(null), useRef(null)
  ];

  const email = location.state?.email;
  const [lang, setLang] = useState(() => getInitialLang(location.state?.lang));
  const i = I18N[lang] || I18N.fr;

  // Event code
  const [eventCode, setEventCode] = useState('');
  const [eventCodeLoading, setEventCodeLoading] = useState(false);
  const [eventCodeError, setEventCodeError] = useState('');
  const [codeRecordId, setCodeRecordId] = useState(null);

  // Profil (pour les nouveaux utilisateurs)
  const [verifiedUser, setVerifiedUser] = useState(null);
  const [profileForm, setProfileForm] = useState({
    firstname: '', lastname: '', fonction: '', description: '', country: '', linkedin: '', phone: ''
  });
  const [countries, setCountries] = useState([]);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);

  // Config sign-in (depuis Content-display)
  // showAccessCode   : affiche le bloc 1 "J'ai un code d'accès"   ← Register = on
  // showOpenRegister : inscription libre sans code               ← Register = nocode
  // showJoinRequest  : affiche le bloc 2 "Demander à rejoindre"  ← Visitor = on
  const [signInConfig, setSignInConfig] = useState({ showAccessCode: true, showOpenRegister: false, showJoinRequest: true });

  // Pré-inscription
  const [preRegForm, setPreRegForm] = useState({
    firstname: '', lastname: '', company: '', fonction: '',
    description: '', country: '', linkedin: '', phone: '',
  });
  const [preRegSaving, setPreRegSaving] = useState(false);
  const [preRegError, setPreRegError] = useState('');
  const [preRegTerms, setPreRegTerms] = useState(false);

  useEffect(() => {
    if (!email) {
      const loginPath = eventSlug ? `/event/${eventSlug}/login` : '/login';
      navigate(loginPath);
    } else {
      inputRefs[0].current?.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, eventSlug, navigate]);

  // Charger les pays et les CGU quand on passe à l'étape profil
  useEffect(() => {
    if (step !== 'profile' && step !== 'pre_register') return;

    if (countries.length === 0) {
      fetch(`${API_BASE_URL}/countries`)
        .then(r => r.json())
        .then(data => { if (data.success) setCountries(data.data || []); })
        .catch(err => console.error('Error loading countries:', err));
    }
  }, [step]);

  // Charger la config sign-in depuis Content-display
  useEffect(() => {
    const subEventId = event?.subevent_id || event?.id;
    if (!subEventId) return;
    fetch(`${API_BASE_URL}/content-display/${subEventId}`)
      .then(r => r.json())
      .then(data => {
        if (!data.success) return;
        const items = data.data || [];
        const isOn = (typeId) => {
          const item = items.find(x => x.type_id === typeId);
          // Si pas de record → activé par défaut (rétrocompatibilité)
          return item ? item.status === 'on' : true;
        };
        const registerStatus = items.find(x => x.type_id === 'Register')?.status ?? 'on';
        setSignInConfig({
          showAccessCode:   registerStatus === 'on',     // Register=on     → code requis
          showOpenRegister: registerStatus === 'nocode', // Register=nocode → inscription libre
          showJoinRequest:  isOn('Visitor'),             // Visitor=on      → demande à rejoindre
        });
      })
      .catch(err => console.warn('[VerifyCodePage] Content-display fetch failed:', err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event?.id]);

  const handleLangChange = (newLang) => {
    setLang(newLang);
    localStorage.setItem('language', newLang);
  };

  // --- ÉTAPE VÉRIFICATION ---

  const handleChange = (index, value) => {
    const digit = value.replace(/[^0-9]/g, '').slice(-1);
    const newCode = [...code];
    newCode[index] = digit;
    setCode(newCode);
    setError('');
    if (digit && index < 5) inputRefs[index + 1].current?.focus();
    if (digit && index === 5 && newCode.every(d => d)) handleVerify(newCode.join(''));
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs[index - 1].current?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/[^0-9]/g, '').slice(0, 6);
    if (pastedData.length === 6) {
      const newCode = pastedData.split('');
      setCode(newCode);
      inputRefs[5].current?.focus();
      handleVerify(pastedData);
    }
  };

  const handleVerify = async (codeString) => {
    setError('');
    setLoading(true);
    try {
      const data = await verifyCode(email, event?.id || '', codeString);
      const userData = data?.user;

      // Stocker les données utilisateur pour les étapes suivantes
      setVerifiedUser(userData);

      const hasEventAccess = userData?.has_event_access === true;
      const isNewUser      = !userData?.airtable_record_id;
      const dashboardPath  = eventSlug ? `/event/${eventSlug}/dashboard` : '/dashboard';

      const { showAccessCode, showOpenRegister, showJoinRequest } = signInConfig;
      const showEventCodeStep = showAccessCode || showJoinRequest;

      if (isNewUser && showOpenRegister) {
        // Register=nocode → inscription libre, profil directement
        setStep('profile');
      } else if (isNewUser && showEventCodeStep) {
        // Au moins un bloc visible → montrer la page event_code
        setStep('event_code');
      } else if (isNewUser) {
        // Register=off ET Visitor=off → inscriptions fermées
        setStep('registration_closed');
      } else {
        // Utilisateur existant → dashboard
        navigate(dashboardPath);
      }
    } catch (err) {
      console.error('Verify code error:', err);
      setError(err.message || 'Code invalide. Réessayez.');
      setCode(['', '', '', '', '', '']);
      inputRefs[0].current?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const codeString = code.join('');
    if (codeString.length !== 6) {
      setError('Veuillez entrer le code à 6 chiffres');
      return;
    }
    handleVerify(codeString);
  };

  // --- ÉTAPE CODE ÉVÉNEMENT ---

  const handleEventCodeSubmit = async (e) => {
    e.preventDefault();
    setEventCodeError('');
    if (!eventCode.trim()) return;

    setEventCodeLoading(true);
    try {
      const res = await fetch('/auth/validate-event-code.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code:               eventCode.trim().toUpperCase(),
          sub_event_id:       event?.id || '',
          email,
          airtable_record_id: verifiedUser?.airtable_record_id || '',
        }),
      });
      const data = await res.json();

      if (!res.ok) {
        const msgMap = {
          invalid_code:  i.event_code_invalid,
          wrong_event:   i.event_code_wrong_event,
          code_full:     i.event_code_full,
        };
        setEventCodeError(msgMap[data?.error] || i.event_code_error);
        return;
      }

      // Stocker le code_record_id pour le lier après la création du profil
      if (data.code_record_id) {
        setCodeRecordId(data.code_record_id);
      }
      // Mettre à jour airtable_record_id si on l'a obtenu lors de la création de l'App-User
      if (data.airtable_record_id && !verifiedUser?.airtable_record_id) {
        setVerifiedUser(prev => ({ ...prev, airtable_record_id: data.airtable_record_id }));
      }
      // Si une company existe déjà sur ce code (autre user), la stocker pour auto-link dans le Dashboard
      if (data.existing_company_id) {
        localStorage.setItem('pending_company_id', data.existing_company_id);
      }

      // Code validé → profil ou dashboard selon config
      const isNewUser = !verifiedUser?.firstname;
      if (isNewUser) {
        setStep('profile');
      } else {
        const dashboardPath = eventSlug ? `/event/${eventSlug}/dashboard` : '/dashboard';
        navigate(dashboardPath);
      }
    } catch (err) {
      console.error('Event code error:', err);
      setEventCodeError(i.event_code_error);
    } finally {
      setEventCodeLoading(false);
    }
  };

  const handleResendCode = async () => {
    setResendLoading(true);
    setResendSuccess(false);
    setError('');
    try {
      await sendCode(email, event?.id || '', event?.name || '');
      setResendSuccess(true);
      setCode(['', '', '', '', '', '']);
      inputRefs[0].current?.focus();
      setTimeout(() => setResendSuccess(false), 5000);
    } catch (err) {
      console.error('Resend code error:', err);
      setError('Impossible de renvoyer le code. Réessayez.');
    } finally {
      setResendLoading(false);
    }
  };

  const handleBack = () => {
    const loginPath = eventSlug ? `/event/${eventSlug}/login` : '/login';
    navigate(loginPath);
  };

  // --- ÉTAPE PROFIL ---

  const handleProfileChange = (field, value) => {
    setProfileForm(prev => ({ ...prev, [field]: value }));
    setProfileError('');
  };

  const handleProfileSave = async (e) => {
    e.preventDefault();
    setProfileError('');

    // Validation champs obligatoires
    if (!profileForm.firstname?.trim() || !profileForm.lastname?.trim() || !profileForm.fonction?.trim()) {
      setProfileError(i.required_error);
      return;
    }

    // Validation CGU
    if (!termsAccepted) {
      setProfileError(i.terms_required);
      return;
    }

    setProfileSaving(true);
    try {
      // Utiliser le record_id Airtable retourné par verify_code.php en priorité
      let recordId = verifiedUser?.airtable_record_id;

      if (!recordId) {
        // Fallback robuste : POST /api/profile (trouve ou crée l'enregistrement App-Users)
        const subEventId = event?.subevent_id || event?.id || '';
        const createRes = await fetch(`${API_BASE_URL}/profile`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, subevent_id: subEventId }),
        });
        const createData = await createRes.json();
        recordId = createData.record_id || null;
      }

      if (!recordId) {
        throw new Error(i.save_error);
      }

      // Enregistrer le profil dans Airtable (+ Networking Status = "1")
      const saveRes = await fetch(`${API_BASE_URL}/profile/${recordId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...profileForm, networking_status: '1' }),
      });
      const saveData = await saveRes.json();

      if (!saveData.success) {
        throw new Error(saveData.error || i.save_error);
      }

      // Synchroniser firstname + lastname dans MySQL (non-bloquant)
      try {
        await fetch('/auth/sync-profile.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email,
            firstname: profileForm.firstname,
            lastname: profileForm.lastname,
          }),
        });
      } catch (syncErr) {
        console.warn('MySQL sync failed (non-blocking):', syncErr);
      }

      // Lier l'App-User au code Registration_Codes (maintenant que le profil existe)
      if (codeRecordId && recordId) {
        try {
          await fetch('/auth/link-code-to-user.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              code_record_id:   codeRecordId,
              airtable_user_id: recordId,
            }),
          });
        } catch (linkErr) {
          console.warn('Link code to user failed (non-blocking):', linkErr);
        }
      }

      // Naviguer vers le dashboard avec le flag newUser
      const dashboardPath = eventSlug ? `/event/${eventSlug}/dashboard` : '/dashboard';
      navigate(dashboardPath, { state: { newUser: true } });

    } catch (err) {
      console.error('Profile save error:', err);
      setProfileError(err.message || i.save_error);
    } finally {
      setProfileSaving(false);
    }
  };

  // --- ÉTAPE PRÉ-INSCRIPTION ---

  const handlePreRegChange = (field, value) => {
    setPreRegForm(prev => ({ ...prev, [field]: value }));
    setPreRegError('');
  };

  const handlePreRegSubmit = async (e) => {
    e.preventDefault();
    setPreRegError('');

    if (!preRegForm.firstname?.trim() || !preRegForm.lastname?.trim() ||
        !preRegForm.company?.trim() || !preRegForm.fonction?.trim()) {
      setPreRegError(i.pre_register_required);
      return;
    }
    if (!preRegTerms) {
      setPreRegError(i.terms_required);
      return;
    }

    setPreRegSaving(true);
    try {
      const subEventId = event?.subevent_id || event?.id || '';
      const res = await fetch(`${API_BASE_URL}/pre-register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...preRegForm, email, sub_event_id: subEventId }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || i.pre_register_error);
      setStep('pre_register_success');
    } catch (err) {
      console.error('Pre-register error:', err);
      setPreRegError(err.message || i.pre_register_error);
    } finally {
      setPreRegSaving(false);
    }
  };

  // Pays triés (Europe en premier)
  const europeanCountries = countries
    .filter(c => EUROPEAN_CODES.has(c.code))
    .sort((a, b) => a.name.localeCompare(b.name));
  const worldCountries = countries
    .filter(c => !EUROPEAN_CODES.has(c.code))
    .sort((a, b) => a.name.localeCompare(b.name));

  // ======= RENDU ÉTAPE PROFIL =======
  if (step === 'profile') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex flex-col">
        <div className="flex-1 flex items-center justify-center px-4 py-10">
          <div className="max-w-lg w-full">
            <div className="bg-white rounded-2xl shadow-xl p-8">

              {/* Header + lang selector */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <User className="text-blue-600" size={20} />
                  </div>
                  <div>
                    <h1 className="text-xl font-bold text-gray-900">{i.profile_title}</h1>
                    <p className="text-xs text-gray-500">{i.profile_subtitle}</p>
                  </div>
                </div>
                <div className="relative">
                  <select
                    value={lang}
                    onChange={(e) => handleLangChange(e.target.value)}
                    className="appearance-none bg-gray-50 border border-gray-200 rounded-lg pl-3 pr-7 py-1.5 text-sm text-gray-700 focus:ring-2 focus:ring-blue-500 cursor-pointer"
                  >
                    {SUPPORTED_LANGS.map(l => (
                      <option key={l} value={l}>{LANG_LABELS[l]}</option>
                    ))}
                  </select>
                  <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                </div>
              </div>

              {/* Event logo */}
              {(event?.logo_dark || event?.logo) && (
                <div className="text-center mb-5">
                  <img src={event.logo_dark || event.logo} alt={event?.name} className="max-h-14 object-contain mx-auto" />
                </div>
              )}

              {/* Erreur */}
              {profileError && (
                <div className="mb-5 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                  <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={18} />
                  <p className="text-sm text-red-700">{profileError}</p>
                </div>
              )}

              {/* Formulaire profil */}
              <form onSubmit={handleProfileSave} className="space-y-4">

                {/* Prénom + Nom */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">{i.firstname}</label>
                    <input
                      type="text"
                      value={profileForm.firstname}
                      onChange={(e) => handleProfileChange('firstname', e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">{i.lastname}</label>
                    <input
                      type="text"
                      value={profileForm.lastname}
                      onChange={(e) => handleProfileChange('lastname', e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      required
                    />
                  </div>
                </div>

                {/* Fonction */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">{i.fonction}</label>
                  <input
                    type="text"
                    value={profileForm.fonction}
                    onChange={(e) => handleProfileChange('fonction', e.target.value)}
                    placeholder="CEO, Sales Manager, Engineer..."
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    required
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">{i.description}</label>
                  <textarea
                    value={profileForm.description}
                    onChange={(e) => handleProfileChange('description', e.target.value)}
                    placeholder={i.description_placeholder}
                    rows={3}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none"
                  />
                </div>

                {/* Pays */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">{i.country}</label>
                  <select
                    value={profileForm.country}
                    onChange={(e) => handleProfileChange('country', e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-sm"
                  >
                    <option value="">{i.country_placeholder}</option>
                    {europeanCountries.length > 0 && (
                      <optgroup label={i.country_europe}>
                        {europeanCountries.map(c => (
                          <option key={c.id} value={c.name}>{c.name}</option>
                        ))}
                      </optgroup>
                    )}
                    {worldCountries.length > 0 && (
                      <optgroup label={i.country_world}>
                        {worldCountries.map(c => (
                          <option key={c.id} value={c.name}>{c.name}</option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                </div>

                {/* LinkedIn + Téléphone */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">{i.linkedin}</label>
                    <input
                      type="url"
                      value={profileForm.linkedin}
                      onChange={(e) => handleProfileChange('linkedin', e.target.value)}
                      placeholder="https://linkedin.com/in/..."
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">{i.phone}</label>
                    <input
                      type="tel"
                      value={profileForm.phone}
                      onChange={(e) => handleProfileChange('phone', e.target.value)}
                      placeholder="+33 6 12 34 56 78"
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    />
                  </div>
                </div>

                {/* Acceptation CGU */}
                <div className="pt-1">
                  <label className="flex items-start gap-2.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={termsAccepted}
                      onChange={e => { setTermsAccepted(e.target.checked); setProfileError(''); }}
                      className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer flex-shrink-0"
                    />
                    <span className="text-sm text-gray-700 leading-snug">
                      {i.terms_agree}{' '}
                      <a
                        href={eventSlug ? `/event/${eventSlug}/terms` : '/terms'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 font-semibold hover:underline"
                      >
                        {i.terms_link}
                      </a>
                    </span>
                  </label>
                </div>

                {/* Bouton valider */}
                <button
                  type="submit"
                  disabled={profileSaving || !termsAccepted}
                  className="w-full mt-2 bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {profileSaving ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      <span>{i.completing}</span>
                    </>
                  ) : (
                    <span>{i.complete_btn}</span>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="bg-white border-t py-4">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <p className="text-sm text-gray-600">Powered by <span className="font-semibold">Mobivent</span></p>
          </div>
        </footer>

      </div>
    );
  }

  // ======= RENDU : SUCCÈS PRÉ-INSCRIPTION =======
  if (step === 'registration_closed') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex flex-col">
        <div className="flex-1 flex items-center justify-center px-4 py-10">
          <div className="max-w-md w-full">
            <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
              {(event?.logo_dark || event?.logo) && (
                <div className="mb-6">
                  <img src={event.logo_dark || event.logo} alt={event?.name} className="max-h-14 object-contain mx-auto" />
                </div>
              )}
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-5">
                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">{i.registration_closed_title}</h2>
              <p className="text-gray-500 text-sm leading-relaxed">{i.registration_closed_msg}</p>
            </div>
          </div>
        </div>
        <footer className="bg-white border-t py-4">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <p className="text-sm text-gray-600">Powered by <span className="font-semibold">Mobivent</span></p>
          </div>
        </footer>
      </div>
    );
  }

  if (step === 'pre_register_success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex flex-col">
        <div className="flex-1 flex items-center justify-center px-4 py-10">
          <div className="max-w-md w-full">
            <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
              {(event?.logo_dark || event?.logo) && (
                <div className="mb-6">
                  <img src={event.logo_dark || event.logo} alt={event?.name} className="max-h-14 object-contain mx-auto" />
                </div>
              )}
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
                <CheckCircle className="text-green-600" size={32} />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">{i.pre_register_success_title}</h2>
              <p className="text-gray-600 text-sm leading-relaxed">{i.pre_register_success_msg}</p>
            </div>
          </div>
        </div>
        <footer className="bg-white border-t py-4">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <p className="text-sm text-gray-600">Powered by <span className="font-semibold">Mobivent</span></p>
          </div>
        </footer>
      </div>
    );
  }

  // ======= RENDU : FORMULAIRE PRÉ-INSCRIPTION =======
  if (step === 'pre_register') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex flex-col">
        <div className="flex-1 flex items-center justify-center px-4 py-10">
          <div className="max-w-lg w-full">
            <div className="bg-white rounded-2xl shadow-xl p-8">

              {/* Header + lang */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <button onClick={() => setStep('event_code')} className="text-gray-400 hover:text-gray-700 transition">
                    <ArrowLeft size={18} />
                  </button>
                  <div>
                    <h1 className="text-xl font-bold text-gray-900">{i.pre_register_title}</h1>
                    <p className="text-xs text-gray-500">{i.pre_register_subtitle}</p>
                  </div>
                </div>
                <div className="relative">
                  <select value={lang} onChange={e => handleLangChange(e.target.value)}
                    className="appearance-none bg-gray-50 border border-gray-200 rounded-lg pl-3 pr-7 py-1.5 text-sm text-gray-700 focus:ring-2 focus:ring-blue-500 cursor-pointer">
                    {SUPPORTED_LANGS.map(l => <option key={l} value={l}>{LANG_LABELS[l]}</option>)}
                  </select>
                  <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                </div>
              </div>

              {(event?.logo_dark || event?.logo) && (
                <div className="text-center mb-5">
                  <img src={event.logo_dark || event.logo} alt={event?.name} className="max-h-12 object-contain mx-auto" />
                </div>
              )}

              {preRegError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                  <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={16} />
                  <p className="text-sm text-red-700">{preRegError}</p>
                </div>
              )}

              <form onSubmit={handlePreRegSubmit} className="space-y-4">

                {/* Prénom + Nom */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">{i.firstname}</label>
                    <input type="text" value={preRegForm.firstname}
                      onChange={e => handlePreRegChange('firstname', e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm" required />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">{i.lastname}</label>
                    <input type="text" value={preRegForm.lastname}
                      onChange={e => handlePreRegChange('lastname', e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm" required />
                  </div>
                </div>

                {/* Société */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">{i.pre_register_company}</label>
                  <input type="text" value={preRegForm.company}
                    onChange={e => handlePreRegChange('company', e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm" required />
                </div>

                {/* Fonction */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">{i.fonction}</label>
                  <input type="text" value={preRegForm.fonction}
                    onChange={e => handlePreRegChange('fonction', e.target.value)}
                    placeholder="CEO, Sales Manager, Engineer..."
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm" required />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">{i.description}</label>
                  <textarea value={preRegForm.description}
                    onChange={e => handlePreRegChange('description', e.target.value)}
                    placeholder={i.description_placeholder} rows={3}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm resize-none" />
                </div>

                {/* Pays */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">{i.country}</label>
                  <select value={preRegForm.country} onChange={e => handlePreRegChange('country', e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white text-sm">
                    <option value="">{i.country_placeholder}</option>
                    {europeanCountries.length > 0 && (
                      <optgroup label={i.country_europe}>
                        {europeanCountries.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                      </optgroup>
                    )}
                    {worldCountries.length > 0 && (
                      <optgroup label={i.country_world}>
                        {worldCountries.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                      </optgroup>
                    )}
                  </select>
                </div>

                {/* LinkedIn + Téléphone */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">{i.linkedin}</label>
                    <input type="url" value={preRegForm.linkedin}
                      onChange={e => handlePreRegChange('linkedin', e.target.value)}
                      placeholder="https://linkedin.com/in/..."
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">{i.phone}</label>
                    <input type="tel" value={preRegForm.phone}
                      onChange={e => handlePreRegChange('phone', e.target.value)}
                      placeholder="+33 6 12 34 56 78"
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm" />
                  </div>
                </div>

                {/* CGU */}
                <div className="pt-1">
                  <label className="flex items-start gap-2.5 cursor-pointer">
                    <input type="checkbox" checked={preRegTerms}
                      onChange={e => { setPreRegTerms(e.target.checked); setPreRegError(''); }}
                      className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer flex-shrink-0" />
                    <span className="text-sm text-gray-700 leading-snug">
                      {i.terms_agree}{' '}
                      <a href={eventSlug ? `/event/${eventSlug}/terms` : '/terms'}
                        target="_blank" rel="noopener noreferrer"
                        className="text-blue-600 font-semibold hover:underline">
                        {i.terms_link}
                      </a>
                    </span>
                  </label>
                </div>

                <button type="submit" disabled={preRegSaving || !preRegTerms}
                  className="w-full mt-2 bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                  {preRegSaving ? (
                    <><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div><span>{i.pre_register_submitting}</span></>
                  ) : (
                    <span>{i.pre_register_submit}</span>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
        <footer className="bg-white border-t py-4">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <p className="text-sm text-gray-600">Powered by <span className="font-semibold">Mobivent</span></p>
          </div>
        </footer>
      </div>
    );
  }

  // ======= RENDU ÉTAPE CODE ÉVÉNEMENT =======
  if (step === 'event_code') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex flex-col">
        <div className="flex-1 flex items-center justify-center px-4 py-12">
          <div className="max-w-md w-full">
            <div className="bg-white rounded-2xl shadow-xl p-8">

              {/* Sélecteur de langue */}
              <div className="flex justify-end mb-4">
                <div className="relative">
                  <select
                    value={lang}
                    onChange={(e) => handleLangChange(e.target.value)}
                    className="appearance-none bg-gray-50 border border-gray-200 rounded-lg pl-3 pr-7 py-1.5 text-sm text-gray-700 focus:ring-2 focus:ring-blue-500 cursor-pointer"
                  >
                    {SUPPORTED_LANGS.map(l => (
                      <option key={l} value={l}>{LANG_LABELS[l]}</option>
                    ))}
                  </select>
                  <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                </div>
              </div>

              {/* Logo événement */}
              {(event?.logo_dark || event?.logo) && (
                <div className="text-center mb-5">
                  <img
                    src={event.logo_dark || event.logo}
                    alt={event?.name || 'Événement'}
                    className="max-h-16 object-contain mx-auto"
                  />
                </div>
              )}

              {/* Nom de l'événement */}
              {event?.name && (
                <p className="text-center text-sm font-semibold text-blue-600 mb-6 tracking-wide uppercase">
                  {event.name}
                </p>
              )}

              {/* Titre + message */}
              <div className="text-center mb-7">
                <h1 className="text-2xl font-bold text-gray-900 mb-3">{i.event_code_welcome}</h1>
                <p className="text-gray-600 text-sm leading-relaxed">{i.event_code_registered}</p>
              </div>

              {/* Erreur */}
              {eventCodeError && (
                <div className="mb-5 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                  <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={18} />
                  <p className="text-sm text-red-700">{eventCodeError}</p>
                </div>
              )}

              {/* Bloc 1 : Code d'accès — affiché si Register: on */}
              {signInConfig.showAccessCode && (
                <div className="border border-gray-200 rounded-xl p-5">
                  <p className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-100 text-blue-600 text-xs font-bold">1</span>
                    {i.event_code_have_code}
                  </p>
                  <form onSubmit={handleEventCodeSubmit} className="space-y-3">
                    <input
                      type="text"
                      value={eventCode}
                      onChange={(e) => { setEventCode(e.target.value.toUpperCase()); setEventCodeError(''); }}
                      placeholder={i.event_code_placeholder}
                      autoFocus
                      disabled={eventCodeLoading}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center text-lg font-mono tracking-widest uppercase disabled:bg-gray-100"
                    />
                    <button
                      type="submit"
                      disabled={eventCodeLoading || !eventCode.trim()}
                      className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {eventCodeLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                          <span>{i.event_code_validating}</span>
                        </>
                      ) : (
                        <span>{i.event_code_btn}</span>
                      )}
                    </button>
                  </form>
                </div>
              )}

              {/* Séparateur OU — uniquement si les deux blocs sont visibles */}
              {signInConfig.showAccessCode && signInConfig.showJoinRequest && (
                <div className="flex items-center my-4 gap-3">
                  <div className="flex-1 h-px bg-gray-200"></div>
                  <span className="text-xs text-gray-400 font-medium uppercase tracking-wider">{i.event_code_or}</span>
                  <div className="flex-1 h-px bg-gray-200"></div>
                </div>
              )}

              {/* Bloc 2 : Demander à rejoindre — affiché si Visitor: on */}
              {signInConfig.showJoinRequest && (
                <div className="border border-gray-200 rounded-xl p-5">
                  <p className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-green-100 text-green-600 text-xs font-bold">2</span>
                    {i.pre_register_btn}
                  </p>
                  <button
                    type="button"
                    onClick={() => setStep('pre_register')}
                    className="w-full border-2 border-green-500 text-green-600 py-3 rounded-lg font-semibold hover:bg-green-50 transition flex items-center justify-center gap-2"
                  >
                    {i.pre_register_btn}
                    <span className="text-xs text-green-400 ml-1">— {i.pre_register_sub}</span>
                  </button>
                </div>
              )}

              {/* Pas de code → contact (uniquement si bloc 1 visible) */}
              {signInConfig.showAccessCode && (
                <div className="mt-5 text-center">
                  <p className="text-sm text-gray-500">
                    {i.event_code_no_code}{' '}
                    <a
                      href="mailto:hello@mobivent.fr"
                      className="text-blue-600 font-semibold hover:underline"
                    >
                      {i.event_code_contact}
                    </a>
                  </p>
                </div>
              )}

            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="bg-white border-t py-4">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <p className="text-sm text-gray-600">Powered by <span className="font-semibold">Mobivent</span></p>
          </div>
        </footer>
      </div>
    );
  }

  // ======= RENDU ÉTAPE VÉRIFICATION (défaut) =======
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <button onClick={handleBack} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition">
            <ArrowLeft size={20} />
            <span>{i.back}</span>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            {/* Logo */}
            {event?.logo && (
              <div className="text-center mb-6">
                <img src={event.logo} alt={event?.name || 'Événement'} className="max-h-16 object-contain mx-auto" />
              </div>
            )}

            {/* Titre */}
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">{i.verify_title}</h1>
              <p className="text-gray-600">{i.verify_subtitle}</p>
              <p className="text-blue-600 font-semibold mt-1">{email}</p>
            </div>

            {/* Erreur */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {/* Succès renvoi */}
            {resendSuccess && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
                <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={20} />
                <p className="text-sm text-green-700">{i.resend_success}</p>
              </div>
            )}

            {/* Saisie du code */}
            <form onSubmit={handleSubmit}>
              <div className="mb-6">
                <div className="flex gap-3 justify-center">
                  {code.map((digit, index) => (
                    <input
                      key={index}
                      ref={inputRefs[index]}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      onPaste={index === 0 ? handlePaste : undefined}
                      disabled={loading}
                      className="w-12 h-14 text-center text-2xl font-bold border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                    />
                  ))}
                </div>
              </div>

              <button
                type="submit"
                disabled={loading || code.some(d => !d)}
                className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>{i.verifying}</span>
                  </>
                ) : (
                  <span>{i.verify_btn}</span>
                )}
              </button>
            </form>

            {/* Renvoi du code */}
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600 mb-2">{i.no_code}</p>
              <button
                onClick={handleResendCode}
                disabled={resendLoading}
                className="text-blue-600 font-semibold hover:underline disabled:text-gray-400 disabled:no-underline"
              >
                {resendLoading ? i.resending : i.resend}
              </button>
            </div>

            {/* Astuce spam */}
            <div className="mt-6 p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-gray-700">
                <span className="font-semibold">💡</span> {i.spam_tip}
              </p>
            </div>
          </div>

          {/* Aide */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Besoin d'aide ?{' '}
              <a href="mailto:hello@mobivent.fr" className="text-blue-600 font-semibold hover:underline">
                Contactez le support
              </a>
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-white border-t py-4">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-sm text-gray-600">Powered by <span className="font-semibold">Mobivent</span></p>
        </div>
      </footer>
    </div>
  );
};

export default VerifyCodePage;
